/**
 * Single source of truth for the site's routes and for the language <-> URL mapping.
 *
 * This file is written in CommonJS on purpose: it is consumed both by the React
 * application (through webpack) and by the Node build scripts
 * (scripts/generate-sitemap.js, scripts/prerender.js). Keeping one module means the
 * sitemap, the prerenderer and the router can never drift apart.
 *
 * ## The route model
 *
 * A route is a stable `key` plus one slug per language. The key is what the app,
 * the copy (`src/seo/content.js`) and the JSON-LD builders pass around; the slug
 * is what the visitor sees. English lives at the slug, Spanish lives at the same
 * slug translated, under the `/es` prefix:
 *
 *   { key: 'moodleAgent', slugs: { en: 'moodle-ai-agent', es: 'agente-ia-moodle' } }
 *     -> /moodle-ai-agent   and   /es/agente-ia-moodle
 *
 * There is deliberately no "language-neutral path": a translated slug cannot be
 * derived from another language's slug, so every lookup goes through the key.
 *
 * The slugs below must stay identical to `seoContent[lang].pages[key].slug`.
 */

var config = require('./config');

var LANGS = ['en', 'es'];
var DEFAULT_LANG = 'en';
var ES_PREFIX = '/es';

/**
 * Every route on the site. `priority` and `changefreq` feed the sitemap.
 * Moodle is the positioning, so the Moodle page carries the highest priority
 * after the home page.
 */
var ROUTES = [
  { key: 'home', slugs: { en: '', es: '' }, changefreq: 'weekly', priority: 1.0 },
  {
    key: 'moodleAgent',
    slugs: { en: 'moodle-ai-agent', es: 'agente-ia-moodle' },
    changefreq: 'weekly',
    priority: 0.9,
  },
  {
    key: 'whatsappAgent',
    slugs: { en: 'whatsapp-ai-agent', es: 'agente-ia-whatsapp' },
    changefreq: 'monthly',
    priority: 0.8,
  },
  {
    key: 'agenticPipelines',
    slugs: { en: 'agentic-pipelines', es: 'pipelines-de-agentes' },
    changefreq: 'monthly',
    priority: 0.8,
  },
  { key: 'about', slugs: { en: 'about', es: 'nosotros' }, changefreq: 'monthly', priority: 0.6 },
  // The two production cases. A slug may contain a slash: the case studies live
  // under a folder segment ("case-studies" / "casos") so the URL says what the
  // page is before the visitor reads the name. Every helper below works on the
  // whole slug, never on a single path segment, so multi-segment slugs need no
  // special handling.
  {
    key: 'caseEstudios',
    slugs: { en: 'case-studies/estudios-tributarios', es: 'casos/estudios-tributarios' },
    changefreq: 'monthly',
    priority: 0.7,
  },
  {
    key: 'caseProsperia',
    slugs: { en: 'case-studies/prosperia', es: 'casos/prosperia' },
    changefreq: 'monthly',
    priority: 0.7,
  },
  {
    key: 'glossary',
    slugs: { en: 'glossary', es: 'glosario' },
    changefreq: 'monthly',
    priority: 0.5,
  },
  { key: 'privacy', slugs: { en: 'privacy', es: 'privacy' }, changefreq: 'yearly', priority: 0.3 },
];

var HOME_KEY = 'home';

/** Normalize a path: always a leading slash, never a trailing one (except "/"). */
function normalize(path) {
  var p = path || '/';
  if (p.charAt(0) !== '/') p = '/' + p;
  if (p.length > 1 && p.charAt(p.length - 1) === '/') p = p.slice(0, -1);
  return p;
}

/** The route record for a key, or undefined. */
function routeByKey(key) {
  for (var i = 0; i < ROUTES.length; i++) {
    if (ROUTES[i].key === key) return ROUTES[i];
  }
  return undefined;
}

/** The slug of a route in a language. Home is the empty slug. */
function slugFor(key, lang) {
  var route = routeByKey(key);
  if (!route) return undefined;
  var slug = route.slugs[lang];
  return slug === undefined ? route.slugs[DEFAULT_LANG] : slug;
}

/** Language implied by a pathname. "/es/agente-ia-moodle" -> "es" */
function langFromPath(pathname) {
  var p = normalize(pathname);
  if (p === ES_PREFIX || p.indexOf(ES_PREFIX + '/') === 0) return 'es';
  return DEFAULT_LANG;
}

/**
 * Localized URL path for a route key.
 * ("moodleAgent", "es") -> "/es/agente-ia-moodle"; ("home", "es") -> "/es".
 * An unknown key falls back to the home page of that language rather than
 * producing a link to nowhere.
 */
function localizedPath(key, lang) {
  var language = lang === 'es' ? 'es' : DEFAULT_LANG;
  var slug = slugFor(key, language);
  if (slug === undefined) slug = slugFor(HOME_KEY, language);
  var prefix = language === 'es' ? ES_PREFIX : '';
  if (!slug) return prefix || '/';
  return prefix + '/' + slug;
}

/**
 * Reverse lookup: any pathname -> { key, lang }, or null when the path is not a
 * route of this site. This replaces the old string surgery on a neutral path.
 */
function routeFromPath(pathname) {
  var p = normalize(pathname);
  var lang = langFromPath(p);
  var slugPath = lang === 'es' ? normalize(p.slice(ES_PREFIX.length)) : p;
  var slug = slugPath === '/' ? '' : slugPath.slice(1);
  for (var i = 0; i < ROUTES.length; i++) {
    if (ROUTES[i].slugs[lang] === slug) return { key: ROUTES[i].key, lang: lang };
  }
  return null;
}

/**
 * The same page in another language. Used by the language toggle.
 * A pathname that matches no route (it should not happen: the router only
 * renders known routes) falls back to the other language's home page instead of
 * producing a 404.
 */
function mirrorPath(pathname, lang) {
  var match = routeFromPath(pathname);
  return localizedPath(match ? match.key : HOME_KEY, lang);
}

/**
 * The absolute en / es / x-default URLs for a route key, ready for hreflang.
 * x-default points at English: it is the site's default language.
 */
function alternates(key) {
  var out = {};
  LANGS.forEach(function (lang) {
    out[lang] = config.SITE_URL + localizedPath(key, lang);
  });
  out['x-default'] = config.SITE_URL + localizedPath(key, DEFAULT_LANG);
  return out;
}

/** Every localized route, flattened. Used by the router, the sitemap and the prerenderer. */
function allLocalizedRoutes() {
  var out = [];
  ROUTES.forEach(function (route) {
    LANGS.forEach(function (lang) {
      out.push({
        key: route.key,
        lang: lang,
        path: localizedPath(route.key, lang),
        changefreq: route.changefreq,
        priority: route.priority,
      });
    });
  });
  return out;
}

module.exports = {
  LANGS: LANGS,
  DEFAULT_LANG: DEFAULT_LANG,
  ES_PREFIX: ES_PREFIX,
  HOME_KEY: HOME_KEY,
  ROUTES: ROUTES,
  normalize: normalize,
  routeByKey: routeByKey,
  slugFor: slugFor,
  langFromPath: langFromPath,
  localizedPath: localizedPath,
  routeFromPath: routeFromPath,
  mirrorPath: mirrorPath,
  alternates: alternates,
  allLocalizedRoutes: allLocalizedRoutes,
};
